import { useState } from 'react'

import { Button, type ButtonSize } from '@/ui/Button'

export interface ConfirmButtonProps {
  /** Base label, e.g. `DELETE`, `WIPE MEMORY`, `DECOMMISSION AGENT`. */
  label: string
  /** Performed on the second (confirming) press. */
  onConfirm: () => void
  /** Fired on the first (arming) press — the app logs the `warn` line here. */
  onArm?: () => void
  size?: ButtonSize
  disabled?: boolean
  className?: string
  /** Optional fixed accessible name (spec 041); absent leaves the visible text as the name. */
  ariaLabel?: string
  /** Optional accessible name while armed; falls back to `ariaLabel`. */
  armedAriaLabel?: string
}

/**
 * Station Kit v2's two-step destructive control (spec 010 §6). It renders as a
 * calm `secondary` button; the first press *arms* it — the label becomes
 * `CONFIRM <label>` and the fill turns `destructive` red — and only the second
 * press performs the action. **Red only ever appears after intent**, which is
 * the whole point: a resting screen never shows a red button.
 *
 * Arming is internal state and per-instance. It resets when the component
 * unmounts, which is exactly "clears on section close or navigation" (§6),
 * because a `SectionPanel` only mounts its body while open and navigation
 * unmounts the tree — no external reset wiring needed. The `onArm` callback is
 * where the app emits the `warn` signal line (`Delete armed for /standup —
 * press again to confirm.`); this component stays presentational and never
 * touches the log store itself.
 */
export function ConfirmButton({
  label,
  onConfirm,
  onArm,
  size = 'default',
  disabled,
  className,
  ariaLabel,
  armedAriaLabel,
}: ConfirmButtonProps) {
  const [armed, setArmed] = useState(false)

  function handleClick() {
    if (!armed) {
      setArmed(true)
      onArm?.()
      return
    }
    setArmed(false)
    onConfirm()
  }

  return (
    <Button
      type="button"
      variant={armed ? 'destructive' : 'secondary'}
      size={size}
      disabled={disabled}
      aria-label={armed ? (armedAriaLabel ?? ariaLabel) : ariaLabel}
      onClick={handleClick}
      className={className}
    >
      {armed ? `CONFIRM ${label}` : label}
    </Button>
  )
}
